import React, { useState } from 'react';
import { providers } from '../../mocks/providers';
import { topCasinos } from '../../mocks/casinos';

export default function ProvidersPage() {
  const [selectedCategory, setSelectedCategory] = useState<string>('Все');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedProvider, setExpandedProvider] = useState<number | null>(null);

  const categories = ['Все', 'Слоты', 'Live казино', 'Джекпоты'];

  const filteredProviders = providers.filter(provider => {
    const matchesCategory = selectedCategory === 'Все' || provider.category === selectedCategory;
    const matchesSearch = provider.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleProvider = (id: number) => {
    setExpandedProvider(expandedProvider === id ? null : id);
  };

  const getCasinosByProvider = (casinoIds: number[]) => {
    return topCasinos.filter(casino => casinoIds.includes(casino.id));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 opacity-10"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Провайдеры игр для онлайн казино
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Полный обзор ведущих разработчиков игрового софта с детальной информацией о каждом провайдере
            </p>
          </div>

          {/* Search and Filter */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                  <input
                    type="text"
                    placeholder="Поиск провайдера..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors text-sm"
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-6 py-3 rounded-xl text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                        selectedCategory === category
                          ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="text-3xl font-bold text-blue-600 mb-2">{providers.length}</div>
              <div className="text-sm text-gray-600">Провайдеров</div>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="text-3xl font-bold text-purple-600 mb-2">
                {providers.reduce((sum, p) => sum + p.games, 0)}+
              </div>
              <div className="text-sm text-gray-600">Игр</div>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="text-3xl font-bold text-pink-600 mb-2">96.4%</div>
              <div className="text-sm text-gray-600">Средний RTP</div>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="text-3xl font-bold text-indigo-600 mb-2">24/7</div>
              <div className="text-sm text-gray-600">Доступность</div>
            </div>
          </div>
        </div>
      </section>

      {/* Providers List */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <p className="text-gray-600">
              Найдено провайдеров: <strong>{filteredProviders.length}</strong>
            </p>
          </div>

          <div className="space-y-6">
            {filteredProviders.map((provider, index) => {
              const isExpanded = expandedProvider === provider.id;
              const availableCasinos = getCasinosByProvider(provider.availableInCasinos);

              return (
                <div
                  key={provider.id}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl"
                  style={{
                    animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                  }}
                >
                  {/* Provider Header */}
                  <div className="p-6 md:p-8">
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Logo */}
                      <div className="flex-shrink-0">
                        <div className="w-48 h-24 bg-gray-50 rounded-xl flex items-center justify-center overflow-hidden">
                          <img
                            src={provider.logo}
                            alt={provider.name}
                            className="w-full h-full object-contain p-4"
                          />
                        </div>
                      </div>

                      {/* Info */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">{provider.name}</h3>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <i className="ri-calendar-line"></i>
                                Основан: {provider.founded}
                              </span>
                              <span className="flex items-center gap-1">
                                <i className="ri-map-pin-line"></i>
                                {provider.headquarters}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 bg-yellow-50 px-4 py-2 rounded-full">
                            <i className="ri-star-fill text-yellow-500"></i>
                            <span className="font-bold text-gray-900">{provider.rating}</span>
                          </div>
                        </div>

                        <p className="text-gray-700 mb-4">{provider.description}</p>

                        <div className="flex flex-wrap gap-3 mb-4">
                          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                            {provider.category}
                          </span>
                          <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                            {provider.games} игр
                          </span>
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                            RTP: {provider.avgRTP}
                          </span>
                        </div>

                        <button
                          onClick={() => toggleProvider(provider.id)}
                          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors cursor-pointer"
                        >
                          {isExpanded ? 'Скрыть детали' : 'Показать детали'}
                          <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line`}></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-gray-50 p-6 md:p-8">
                      {/* Detailed Description */}
                      <div className="mb-8">
                        <h4 className="text-xl font-bold text-gray-900 mb-4">О провайдере</h4>
                        <p className="text-gray-700 leading-relaxed">{provider.detailedDescription}</p>
                      </div>

                      {/* Popular Games */}
                      <div className="mb-8">
                        <h4 className="text-xl font-bold text-gray-900 mb-4">Популярные игры</h4>
                        <div className="flex flex-wrap gap-2">
                          {provider.popularGames.map((game, idx) => (
                            <span
                              key={idx}
                              className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:border-blue-500 transition-colors cursor-pointer"
                            >
                              {game}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Screenshots */}
                      {provider.screenshots && provider.screenshots.length > 0 && (
                        <div className="mb-8">
                          <h4 className="text-xl font-bold text-gray-900 mb-4">Скриншоты игр</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {provider.screenshots.map((screenshot, idx) => (
                              <div key={idx} className="rounded-xl overflow-hidden shadow-md">
                                <img
                                  src={screenshot}
                                  alt={`Скриншот ${idx + 1}`}
                                  className="w-full h-64 object-cover"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Technical Info */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <div>
                          <h5 className="font-bold text-gray-900 mb-3">Лицензии</h5>
                          <div className="flex flex-wrap gap-2">
                            {provider.licenses.map((license, idx) => (
                              <span
                                key={idx}
                                className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm"
                              >
                                {license}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h5 className="font-bold text-gray-900 mb-3">Технологии</h5>
                          <div className="flex flex-wrap gap-2">
                            {provider.technologies.map((tech, idx) => (
                              <span
                                key={idx}
                                className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                              >
                                {tech}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h5 className="font-bold text-gray-900 mb-3">Особенности</h5>
                          <div className="flex flex-wrap gap-2">
                            {provider.features.map((feature, idx) => (
                              <span
                                key={idx}
                                className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                              >
                                {feature}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Available Casinos */}
                      <div>
                        <h4 className="text-xl font-bold text-gray-900 mb-4">
                          Казино с играми {provider.name} ({availableCasinos.length})
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {availableCasinos.map((casino) => (
                            <div
                              key={casino.id}
                              className="bg-white rounded-xl p-4 border border-gray-200 hover:border-blue-500 hover:shadow-md transition-all cursor-pointer"
                            >
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                                  <img
                                    src={casino.image}
                                    alt={casino.name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h5 className="font-bold text-gray-900 truncate">{casino.name}</h5>
                                  <div className="flex items-center gap-1">
                                    <i className="ri-star-fill text-yellow-500 text-sm"></i>
                                    <span className="text-sm text-gray-600">{casino.rating}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="space-y-2 text-sm text-gray-600">
                                <div className="flex items-center gap-2">
                                  <i className="ri-gift-line text-blue-600"></i>
                                  <span>{casino.bonus}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <i className="ri-gamepad-line text-purple-600"></i>
                                  <span>{casino.games} игр</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <i className="ri-time-line text-green-600"></i>
                                  <span>Вывод: {casino.withdrawTime}</span>
                                </div>
                              </div>
                              <button className="w-full mt-4 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all whitespace-nowrap cursor-pointer text-sm font-medium">
                                Играть в казино
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {filteredProviders.length === 0 && (
            <div className="text-center py-16">
              <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-xl text-gray-600">Провайдеры не найдены</p>
              <p className="text-gray-500 mt-2">Попробуйте изменить параметры поиска</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Готовы попробовать игры от лучших провайдеров?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Выберите казино из нашего рейтинга и получите эксклюзивные бонусы
          </p>
          <button className="px-8 py-4 bg-white text-blue-600 rounded-full text-lg font-bold hover:shadow-2xl transition-all whitespace-nowrap cursor-pointer">
            Перейти к рейтингу казино
          </button>
        </div>
      </section>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}